import {
  AGETH,
  AVAX,
  contractConfig,
  ETH,
  ETHX,
  RSETH,
  SFRXETH,
  SONIC,
  USDT,
  STETH,
  TAGS,
  USDC,
  WAGETH,
  WETH,
  WETHE,
  WRSETH,
  WSTETH,
} from "@config/contractsConfig";
import { formatEther } from "@ethersproject/units";
import { supportedChains } from "@services/WagmiProvider";
import {
  avalanche,
  mantle,
  monad,
  plasma,
  sonic,
  tac,
  stable,
} from "viem/chains";

export const WALLET_KEYS = {
  COINBASEWALLET: "coinbasewallet",
  METAMASK: "metamask",
  WALLETCONNECT: "walletconnect",
  TRUSTWALLET: "trustwallet",
  SAFEPAL: "safepal",
  MATHWALLET: "mathwallet",
  LEDGER_LIVE: "ledgerLive",
  RABBYWALLET: "rabbywallet",
  OKX: "okx",
};

export const HIDDEN_WALLETS = [WALLET_KEYS.LEDGER_LIVE];

export const wallets = [
  WALLET_KEYS.METAMASK,
  WALLET_KEYS.OKX,
  WALLET_KEYS.WALLETCONNECT,
  WALLET_KEYS.COINBASEWALLET,
  WALLET_KEYS.RABBYWALLET,
  WALLET_KEYS.MATHWALLET,
  WALLET_KEYS.TRUSTWALLET,
  WALLET_KEYS.SAFEPAL,
  WALLET_KEYS.LEDGER_LIVE,
];

export interface IIndexable {
  [key: string]: any;
}

export interface IIndexableNumber {
  [key: number]: any;
}

export const walletLabelMapping: IIndexable = {
  injected: "Metamask",
  metamask: "Metamask",
  coinbasewallet: "Coinbase Wallet",
  walletconnect: "Wallet Connect",
  trustwallet: "Trust Wallet",
  safepal: "SafePal Wallet",
  mathwallet: "Math Wallet",
  ledgerLive: "Ledger Live",
  rabbywallet: "Rabby Wallet",
  okex: "OKX Wallet",
};

/**
 * Falls back to the generic injected icon. Connectors the app registers itself
 * carry no `icon` of their own, so the id is what identifies them here.
 */
export const getWalletIcon = (connectorId?: string): string =>
  walletIconMapping[connectorId?.toLowerCase() ?? ""] ||
  walletIconMapping.injected;

export const walletIconMapping: IIndexable = {
  injected: "/assets/wallets/injected.svg",
  metamask: "/assets/wallets/mm.png",
  coinbasewallet: "/assets/wallets/cbw.png",
  walletconnect: "/assets/wallets/wc.png",
  trustwallet: "/assets/wallets/trust.jpeg",
  safepal: "/assets/wallets/safepal.jpeg",
  mathwallet: "/assets/wallets/math.png",
  ledgerlive: "/assets/wallets/ledger.png",
  rabbywallet: "/assets/wallets/rabby.jpeg",
  okex: "/assets/wallets/okex.png",
};

export const TXN_TYPES: IIndexable = {
  TOKEN_APPROVE: "Token_approve",
  ERC20_APPROVE: "ERC20_approve",
  XTOKEN_APPROVE: "XToken_approve",
  STAKE: "Stake",
  UNSTAKE: "Unstake",
  CLAIM: "Claim",
  XTOKEN_ASSOCIATE: "XToken_associate",
  VAULT_DEPOSIT: "Vault_deposit",
  VAULT_WITHDRAW: "Vault_withdraw",
  VAULT_CLAIM: "Vault_claim",
  STABLE_VAULT_CLAIM: "Stable_vault_claim",
  YIELD_VAULT_DEPOSIT: "Yield_vault_deposit",
  YIELD_VAULT_WITHDRAW: "Yield_vault_withdraw",
  YIELD_VAULT_CLAIM: "Yield_vault_claim",
  YIELD_VAULT_XTOKEN_APPROVE: "Yield_vault_xtoken_approve",
  STABLE_VAULT_DEPOSIT: "Stable_vault_deposit",
  STABLE_VAULT_WITHDRAW: "Stable_vault_withdraw",
  CRYPTANIUM_VAULT_DEPOSIT: "Cryptanium_vault_deposit",
  CRYPTANIUM_VAULT_WITHDRAW: "Cryptanium_vault_withdraw",
  CRYPTANIUM_VAULT_CLAIM: "Cryptanium_vault_claim",
  CRYPTANIUM_VAULT_XTOKEN_APPROVE: "Cryptanium_vault_xtoken_approve",
  CLAIM_EL_POINTS: "Claim_el",
  CLAIM_SCROLL_TOKEN: "Claim_scroll",
  CLAIM_KERNEL_AIRDROPS: "Claim_kernel_airdrops",
  WRAP: "wrap",
  UNWRAP: "unwrap",
};

export const TXN_STATUSES: IIndexable = {
  PENDING: "PENDING",
  SUCCESS: "SUCCESS",
  ERROR: "ERROR",
};

export const CONNECT_WALLET_CTA = "Connect Wallet";
export const RESTAKE_NOW = "Restake now";
export const DEPOSIT_NOW = "Deposit now";
export const UNSTAKE_NOW = "Unstake now";
export const WITHDRAW_CTA = "Withdraw";
export const CLAIM_KEP_CTA = "Claim KEP";
export const APPROVE_CTA = "Approve";
export const SWITCH_NETWORK = "Switch Network";
export const CLAIM_EL = "Claim Eigen";
export const CLAIM_SCROLL = "Claim Scroll";

// Unstake Confirmation CTAs
export const SKIP_REWARDS_UNSTAKE_CTA = "Skip Rewards & Unstake";
export const EARN_TOP_REWARDS_CTA = "Earn Top Rewards";

export const formatEtherNumber = (value: string | undefined, decimals = 4) => {
  if (!value) {
    return -1;
  }
  const number = toFixedWithoutRounding(
    formatEther(value.toString()),
    decimals
  );

  return +number;
};

export const toFixedWithoutRounding = (value: string, decimals: number) => {
  if (!value || typeof value !== "string") {
    return "0";
  }

  // Convert exponential notation to standard decimal notation
  let normalizedValue = value;
  if (value.toLowerCase().includes("e")) {
    const num = parseFloat(value);
    if (isNaN(num)) {
      return "0";
    }
    // toLocaleString never falls back to exponential notation. num.toFixed(20)
    // does for values >= 1e21 (e.g. "1e+21"), which the regex below then
    // truncated to a bare "1". Force plain fixed notation with 20 fraction
    // digits so both very large and very small magnitudes round-trip correctly.
    normalizedValue = num.toLocaleString("en-US", {
      useGrouping: false,
      minimumFractionDigits: 20,
      maximumFractionDigits: 20,
    });
  }

  const match = normalizedValue.match(
    new RegExp(`^-?\\d+(?:\\.\\d{0,${decimals}})?`)
  );
  return match ? match[0] : "0";
};

export const isNumber = (str: string) => {
  if (str.toString().trim() === "") {
    return false;
  }
  return !isNaN(+str);
};

export const APPROVAL_AMOUNT = "100000";

export const GAIN_DEPOSIT_VAULT: "ageth" | "hgeth" =
  process.env.NEXT_PUBLIC_GAIN_DEPOSIT_VAULT === "ageth" ? "ageth" : "hgeth";

export const MAX_FRACTION_DIGITS = 4;

export const COMMUNITY_DETAILS = [
  {
    key: "telegram",
    name: "Telegram",
    link: "https://t.me/KelpDAOxyz",
  },
  {
    key: "discord",
    name: "Discord",
    link: "https://discord.com/invite/wfDdcmbMjN",
  },
  {
    key: "twitter",
    name: "Twitter",
    link: "https://twitter.com/KelpDAO",
  },
  {
    key: "docs",
    name: "Docs",
    link: "https://kerneldao.gitbook.io/kernel/",
  },
];

export const SUB_HEADER_OPTIONS = [
  // ...COMMUNITY_DETAILS,
  {
    key: "tos",
    name: "Terms of service",
    link: "/terms-of-service",
  },
  {
    key: "risk",
    name: "Risks & Disclaimer",
    link: "https://kerneldao.gitbook.io/kernel/getting-started/kelp/risk-and-disclaimer",
  },
];

export const getCommunityIcon = (community: string) => {
  switch (community) {
    case "twitter":
      return "/assets/socials/twitter.svg";
    case "telegram":
      return "/assets/socials/telegram.svg";
    case "discord":
      return "/assets/socials/discord.svg";
    case "docs":
      return "/assets/socials/docs.svg";
    case "tos":
      return "/assets/socials/document-hammer.svg";
    case "risk":
      return "/assets/socials/risk.svg";
  }
};

export type LstOptions = {
  key: string;
  title?: string;
  tags?: TAGS[] | [];
  isDisabled?: boolean;
  tag?: string;
  priority?: number;
};

export function getLSTOptionsForChain(
  chainId: number,
  hideETH = false,
  isStaderLedgerUser = false,
  isVaultPage = false,
  isCryptaniumVaultPage = false
): LstOptions[] {
  const { nativeToken, tokens, xtoken } =
    contractConfig[chainId] || contractConfig[supportedChains[0].id];

  let list: any = [];

  // For vault pages, check if native token supports vaults
  const shouldIncludeETH =
    !hideETH &&
    chainId !== tac.id &&
    chainId !== avalanche.id &&
    chainId !== plasma.id &&
    chainId !== sonic.id &&
    chainId !== stable.id &&
    chainId !== mantle.id &&
    chainId !== monad.id &&
    (isVaultPage ? nativeToken.isVaultSupported : true) &&
    !isCryptaniumVaultPage;

  if (shouldIncludeETH) {
    list.push({
      title: nativeToken.symbol,
      key: nativeToken.symbol,
      priority: 0,
      tags: nativeToken.tags,
      ...("unstakePriority" in nativeToken
        ? { unstakePriority: nativeToken.unstakePriority }
        : {}),
    });
  }

  const tokenList = Object.keys(tokens).length > 0 ? Object.keys(tokens) : [];

  list = [
    ...list,
    ...tokenList
      .filter((key) => {
        return (
          (isStaderLedgerUser ? tokens[key].isLiquidStakeSupported : true) &&
          tokens[key].symbol != "sfrxETH"
        );
      })
      .map((key) => ({
        key,
        title: key,
        tags: tokens[key].tags,
        priority: tokens[key].priority || 0,
        unstakePriority: tokens[key].unstakePriority || 0,
      })),
  ];

  if (isVaultPage) {
    list.push({
      key: xtoken.symbol,
      title: xtoken.symbol,
      tags: [],
      priority: -3,
      unstakePriority: -1,
    });
    list = list.map((data: any) => {
      return {
        ...data,
        priority: data.key === "ETH" ? -2 : data?.priority || 0,
      };
    });
  } else {
    list.push(
      {
        key: "divider",
        tags: [],
        title: "",
      },
      {
        title: "Other LSTs",
        key: "other",
        tag: "Coming soon",
        isDisabled: true,
      }
    );
  }

  return list;
}

export const LSTIconMapping: IIndexable = {
  [STETH]: "/assets/stETH.svg",
  [ETHX]: "/assets/ethx.svg",
  [SFRXETH]: "/assets/sfrx.svg",
  [ETH]: "/assets/eth.svg",
  [WETH]: "/assets/eth.svg",
  [WETHE]: "/assets/eth.svg",
  [WSTETH]: "/assets/stETH.svg",
  rsETH: "/assets/rseth.svg",
  wrsETH: "/assets/wrseth.svg",
  [WAGETH]: "/assets/ageth.svg",
  hgETH: "/assets/vaults/hgeth.svg",
  [USDT]: "/assets/vaults/usdt.svg",
  [USDC]: "/assets/vaults/usdc.svg",
  sbUSD: "/assets/vaults/stableVault.svg",
  KERNEL: "/assets/kernel.svg",
  [AVAX]: "/assets/chains/avalanche.png",
  [SONIC]: "/assets/chains/sonic.png",
  xgUSD: "/assets/vaults/usdt.svg",
};

export const VaultTokenIconMapping: IIndexable = {};

export const WrapBridgeMappings = {
  TOKENS: {
    [RSETH]: RSETH,
    [WRSETH]: WRSETH,
    ["agETH"]: AGETH,
    ["wagETH"]: WAGETH,
  } as IIndexable,
  ICONS: {
    [RSETH]: "/assets/rseth.png",
    [WRSETH]: "/assets/wrseth.svg",
    [AGETH]: "/assets/ageth.svg",
    [WAGETH]: "/assets/ageth.svg",
    ["wagETH"]: "/assets/ageth.svg",
    placeholder: "/assets/placeholder.svg",
    1: "/assets/chains/ethereum.svg",
    42161: "/assets/chains/arbitrum.svg",
    81457: "/assets/chains/blast.png",
    34443: "/assets/chains/mode.svg",
    534352: "/assets/chains/scroll.svg",
    10: "/assets/chains/optimism.svg",
    8453: "/assets/chains/base.svg",
    59144: "/assets/chains/linea.svg",
    324: "/assets/chains/zksync.svg",
    169: "/assets/bridge/manta-pacific.svg",
    196: "/assets/chains/xlayer.svg",
    48900: "/assets/chains/zircuit.svg",
    239: "/assets/chains/tac.svg",
    130: "/assets/chains/unichain.svg",
    43114: "/assets/chains/avalanche.png",
    146: "/assets/chains/sonic.png",
    9745: "/assets/chains/plasma.png",
    57073: "/assets/chains/ink.svg",
    988: "/assets/chains/stable.webp",
    5000: "/assets/chains/mantle.svg",
    143: "/assets/chains/monad.png",
    4326: "/assets/chains/megaeth.webp",
  } as IIndexable,
};

export enum SidebarTag {
  COMING_SOON = "Coming Soon",
  NEW = "New",
  HOT = "Hot",
  INDICATOR = "Indicator",
}
export type SidebarNavItem = {
  title: string;
  key: string;
  link: string;
  icon: string;
  tag?: SidebarTag;
  disableForLedger?: boolean;
  activeIcon: string;
  isExternal?: boolean;
};

export type SidebarNav = {
  key: string;
  title: string | null;
  icon: {
    src: string;
    alt: string;
    height: number;
    width: number;
  } | null;
  rightIcon?: string;
  items?: SidebarNavItem[];
  link?: string;
  disableForLedger?: boolean;
  tag?: SidebarTag;
  isExternal?: boolean;
};

export const SidebarNavs: SidebarNav[] = [
  {
    key: "liquid-restaking",
    title: "Liquid Restaking",
    icon: null,
    items: [
      {
        title: "Restake",
        key: "restake",
        link: "/restake",
        icon: "/assets/sidebar/inActive/dropHalf.svg",
        activeIcon: "/assets/sidebar/active/dropHalf.svg",
      },
      {
        title: "Bridge & Wrap",
        key: "wrap-bridge",
        link: "/bridge-wrap",
        icon: "/assets/sidebar/inActive/bridge.svg",
        activeIcon: "/assets/sidebar/active/bridge.svg",
        disableForLedger: true,
      },
      // {
      //   title: "Wrap",
      //   key: "wrap",
      //   link: "/wrap",
      //   icon: "/assets/sidebar/inActive/flipHorizontal.svg",
      //   activeIcon: "/assets/sidebar/active/flipHorizontal.svg",
      // },
      {
        title: "AVS",
        key: "avs",
        link: "/avs",
        icon: "/assets/sidebar/inActive/sphere.svg",
        activeIcon: "/assets/sidebar/active/sphere.svg",
      },
    ],
  },
  {
    key: "gain",
    title: null,
    icon: {
      src: "/assets/sidebar/gain.svg",
      alt: "gain",
      height: 24,
      width: 31,
    },
    // tag: SidebarTag.NEW,
    disableForLedger: true,
    items: [
      {
        title: "All Vaults",
        key: "all-vaults",
        link: "https://gain.kerneldao.com/",
        icon: "/assets/sidebar/inActive/vault.svg",
        activeIcon: "/assets/sidebar/active/vault.svg",
      },
      {
        title: "Bridge & Wrap agETH",
        key: "wrap-bridge-ageth",
        link: "/bridge-wrap-ageth",
        icon: "/assets/sidebar/inActive/bridge.svg",
        activeIcon: "/assets/sidebar/active/bridge.svg",
      },
    ],
  },
  {
    key: "defi",
    title: "DeFi",
    icon: null,
    rightIcon: "/assets/sidebar/defi.svg",
    link: "/defi",
  },
  {
    key: "portfolio",
    title: "Portfolio",
    icon: null,
    items: [
      {
        title: "My Rewards",
        key: "my-rewards",
        link: "/dashboard",
        icon: "/assets/sidebar/inActive/chartPie.svg",
        activeIcon: "/assets/sidebar/active/chartPie.svg",
      },
      {
        title: "Claim Token",
        key: "claim-token",
        link: "/claim-token",
        icon: "/assets/sidebar/inActive/clockCountdown.svg",
        activeIcon: "/assets/sidebar/active/clockCountdown.svg",
      },
      {
        title: "Stake $KERNEL",
        key: "stake kernel",
        link: "https://kerneldao.com/stake/",
        isExternal: true,
        icon: "/assets/sidebar/handcoins.svg",
        activeIcon: "/assets/sidebar/handcoins.svg",
      },
    ],
  },
];

export const AUDITS_DATA = [
  {
    key: "sigmaprime",
    imgLink: "/assets/landingPage/audits/sigma_prime.svg",
    link: "https://kelpdao.xyz/audits/smartcontracts/SigmaPrime.pdf",
  },
  {
    key: "coderena",
    imgLink: "/assets/landingPage/audits/coderena.svg",
    link: "https://code4rena.com/reports/2023-11-kelp",
  },
];

export const REWARDS_DISTRIBUTION_DATA = [
  {
    title: "Ethereum rsETH Holders",
    kelpKey: "kelpMilesForRsEthHoldings",
    elpKey: "elPointsForRsEthHoldings",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/chains/ethereum.svg",
  },
  {
    title: "Arbitrum rsETH Holders",
    kelpKey: "kelpMilesForArbitrumHolder",
    elpKey: "elPointsForArbitrumHolder",
    isComingSoon: false,
    tags: ["Arbitrum"],
    icon: "/assets/chains/arbitrum.svg",
  },
  {
    title: "Optimism rsETH Holders",
    kelpKey: "kelpMilesForOPRsEthHoldings",
    elpKey: "elPointsForOPRsEthHoldings",
    isComingSoon: false,
    tags: ["Optimism"],
    icon: "/assets/chains/optimism.svg",
  },
  {
    title: "Optimism wrsETH Holders",
    kelpKey: "kelpMilesForOPWRsEthHoldings",
    elpKey: "elPointsForOPWRsEthHoldings",
    isComingSoon: false,
    tags: ["Optimism"],
    icon: "/assets/chains/optimism.svg",
  },
  {
    title: "Base rsETH Holders",
    kelpKey: "kelpMilesForBaseRsEthHoldings",
    elpKey: "elPointsForBaseRsEthHoldings",
    isComingSoon: false,
    tags: ["Base"],
    icon: "/assets/chains/base.svg",
  },
  {
    title: "Base wrsETH Holders",
    kelpKey: "kelpMilesForBaseWRsEthHoldings",
    elpKey: "elPointsForBaseWRsEthHoldings",
    isComingSoon: false,
    tags: ["Base"],
    icon: "/assets/chains/base.svg",
  },
  {
    title: "Mode rsETH Holders",
    kelpKey: "kelpMilesForModeRsEthHoldings",
    elpKey: "elPointsForModeRsEthHoldings",
    isComingSoon: false,
    tags: ["Mode"],
    icon: "/assets/chains/mode.svg",
  },
  {
    title: "Mode wrsETH Holders",
    kelpKey: "kelpMilesForModeWRsEthHoldings",
    elpKey: "elPointsForModeWRsEthHoldings",
    isComingSoon: false,
    tags: ["Mode"],
    icon: "/assets/chains/mode.svg",
  },
  {
    title: "Scroll rsETH Holders",
    kelpKey: "kelpMilesForScrollRsEthHoldings",
    elpKey: "elPointsForScrollRsEthHoldings",
    isComingSoon: false,
    tags: ["Scroll"],
    icon: "/assets/chains/scroll.svg",
  },
  {
    title: "Scroll wrsETH Holders",
    kelpKey: "kelpMilesForScrollWRsEthHoldings",
    elpKey: "elPointsForScrollWRsEthHoldings",
    isComingSoon: false,
    tags: ["Scroll"],
    icon: "/assets/chains/scroll.svg",
  },
  {
    title: "Linea rsETH Holders",
    kelpKey: "kelpMilesForLineaRsEthHoldings",
    elpKey: "elPointsForLineaRsEthHoldings",
    isComingSoon: false,
    tags: ["Linea"],
    icon: "/assets/chains/linea.svg",
  },
  {
    title: "Linea wrsETH Holders",
    kelpKey: "kelpMilesForLineaWRsEthHoldings",
    elpKey: "elPointsForLineaWRsEthHoldings",
    isComingSoon: false,
    tags: ["Linea"],
    icon: "/assets/chains/linea.svg",
  },
  {
    title: "100k Bonus",
    kelpKey: "kelpMilesForBonus",
    elpKey: null,
    isComingSoon: false,
    tags: [],
    icon: "/assets/kelp_icon.svg",
  },
  {
    title: "Pendle",
    kelpKey: "kelpMilesForPendle",
    elpKey: "elPointsForPendle",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/platforms/pendle.svg",
  },
  {
    title: "Pendle (25th April)",
    kelpKey: "kelpMilesForPendleArbitrum",
    elpKey: "elPointsForPendleArbitrum",
    isComingSoon: false,
    tags: ["Arbitrum"],
    icon: "/assets/platforms/pendle.svg",
  },
  {
    title: "Pendle (27th June)",
    kelpKey: "kelpMilesForPendleArb27thJune",
    elpKey: "elPointsForPendleArb27thJune",
    isComingSoon: false,
    tags: ["Arbitrum"],
    icon: "/assets/platforms/pendle.svg",
  },
  {
    title: "Zircuit",
    kelpKey: "kelpMilesForZircuitrsETHPendle",
    elpKey: "elPointsForZircuitrsETHPendle",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/platforms/zircuit.svg",
  },
  {
    title: "Uniswap (rsETH/ETHx)",
    kelpKey: "kelpMilesForUni",
    elpKey: "elPointsForUni",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/platforms/uni.svg",
  },
  {
    title: "Uniswap (rsETH/WETH)",
    kelpKey: "kelpMilesForUniWEth",
    elpKey: "elPointsForUniWEth",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/platforms/uni.svg",
  },
  {
    title: "Penpie voters",
    kelpKey: "kelpMilesForPenpieETHVoters",
    elpKey: null,
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/platforms/penpie.png",
  },
  {
    title: "Penpie voters",
    kelpKey: "kelpMilesForPenpieARBVoters",
    elpKey: null,
    isComingSoon: false,
    tags: ["Arbitrum"],
    icon: "/assets/platforms/penpie.png",
  },
  {
    title: "Equilibria voters",
    kelpKey: "kelpMilesForEquilbriaVotes",
    elpKey: null,
    isComingSoon: false,
    tags: ["Ethereum", "Arbitrum"],
    icon: "/assets/platforms/equilibria.png",
  },
  {
    title: "Curve",
    kelpKey: "kelpMilesForCurve",
    elpKey: "elPointsForCurve",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/platforms/curve.svg",
  },
  {
    title: "Gearbox",
    kelpKey: "kelpMilesForGearbox",
    elpKey: "elPointsForGearbox",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/platforms/gearbox.png",
  },
  {
    title: "Maverick",
    kelpKey: "kelpMilesForMaverick",
    elpKey: "elPointsForMaverick",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/platforms/maverick.svg",
  },
  {
    title: "Balancer (rsETH/ETHx)",
    kelpKey: "kelpMilesForBalancer",
    elpKey: "elPointsForBalancer",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/platforms/balancer.svg",
  },
  {
    title: "Balancer (rsETH/WETH)",
    kelpKey: "kelpMilesForBalancerWEth",
    elpKey: "elPointsForBalancerWEth",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/platforms/balancer.svg",
  },
  {
    title: "Balancer (rsETH/WETH)",
    kelpKey: "kelpMilesForBalancerZKEVM",
    elpKey: "elPointsForBalancerZKEVM",
    isComingSoon: false,
    tags: ["Polygon zkEVM"],
    icon: "/assets/platforms/balancer.svg",
  },
  {
    title: "Zircuit",
    kelpKey: "kelpMilesForZircuitnDeposit",
    elpKey: "elPointsForZircuitnDeposit",
    isComingSoon: false,
    tags: [],
    icon: "/assets/platforms/zircuit.svg",
  },
  {
    title: "VaultCraft",
    kelpKey: "kelpMilesForVault",
    elpKey: "elPointsForVault",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/platforms/vault.svg",
  },
  {
    title: "Prisma",
    kelpKey: "kelpMilesForPrisma",
    elpKey: "elPointsForPrisma",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/platforms/prisma.png",
  },
  {
    title: "Ramses",
    kelpKey: "kelpMilesForRamses",
    elpKey: "elPointsForRamses",
    isComingSoon: false,
    tags: ["Arbitrum"],
    icon: "/assets/platforms/ramses.png",
  },
  {
    title: "Ambient",
    kelpKey: "kelpMilesForScrollAmbient",
    elpKey: "elPointsForScrollAmbient",
    isComingSoon: false,
    tags: ["Scroll"],
    icon: "/assets/platforms/ambient.png",
  },
  {
    title: "Camelot",
    kelpKey: "kelpMilesForCamelot",
    elpKey: "elPointsForCamelot",
    isComingSoon: false,
    tags: ["Arbitrum"],
    icon: "/assets/platforms/camelot.png",
  },
  {
    title: "Kelp Karnival",
    kelpKey: "kelpMilesForKelpKarnival",
    elpKey: "elPointsForKelpKarnival",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/kelp_icon.svg",
  },
  {
    title: "Referral bonus",
    kelpKey: "kelpMilesForReferrals",
    elpKey: null,
    isComingSoon: false,
    tags: [],
    icon: "/assets/kelp_icon.svg",
  },
  {
    title: "Airpuff rsETH Holders",
    kelpKey: "kelpMilesForAirpuffRsEthHoldings",
    elpKey: "elPointsForAirpuffRsEthHoldings",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/platforms/airpuff.png",
  },
  {
    title: "Airpuff (Arb/rsEth) Holders",
    kelpKey: "kelpMilesForAirpuffArbRsEthHoldings",
    elpKey: "elPointsForAirpuffArbRsEthHoldings",
    isComingSoon: false,
    tags: ["Arbitrum"],
    icon: "/assets/platforms/airpuff.png",
  },
  {
    title: "Beefy",
    kelpKey: "kelpMilesForBeefy",
    elpKey: "elPointsForBeefy",
    isComingSoon: false,
    tags: ["Arbitrum"],
    icon: "/assets/platforms/beefy.png",
  },
  {
    title: "Thruster",
    kelpKey: "kelpMilesForThruster",
    elpKey: "elPointsForThruster",
    isComingSoon: false,
    tags: ["Blast"],
    icon: "/assets/platforms/thurster.png",
  },
  {
    title: "Thruster2",
    kelpKey: "kelpMilesForThruster2",
    elpKey: "elPointsForThruster2",
    isComingSoon: false,
    tags: ["Blast"],
    icon: "/assets/platforms/thurster.png",
  },
  {
    title: "PolygonZkEVM rsEth Holdings",
    kelpKey: "kelpMilesForPolygonZkEvmRsEthHoldings",
    elpKey: "elPointsForPolygonZkEvmRsEthHoldings",
    isComingSoon: false,
    tags: ["Polygon zkEVM"],
    icon: "/assets/chains/polygon.svg",
  },
  {
    title: "Kim",
    kelpKey: "kelpMilesForKim",
    elpKey: "elPointsForKim",
    isComingSoon: false,
    tags: ["Mode"],
    icon: "/assets/platforms/kim.jpg",
  },
  {
    title: "Karak",
    kelpKey: "kelpMilesForKarak",
    elpKey: "elPointsForKarak",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/platforms/karak.svg",
  },
  {
    title: "Stella",
    kelpKey: "kelpMilesForStella",
    elpKey: "elPointsForStella",
    isComingSoon: false,
    tags: ["Arbitrum"],
    icon: "/assets/platforms/stella.jpg",
  },
  {
    title: "BlastAmbient",
    kelpKey: "kelpMilesForBlastAmbient",
    elpKey: "elPointsForBlastAmbient",
    isComingSoon: false,
    tags: ["Blast"],
    icon: "/assets/chains/ambient.png",
  },
  {
    title: "ZeroLend",
    kelpKey: "kelpMilesForZeroLend",
    elpKey: "elPointsForZeroLend",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/platforms/zerolend.jpg",
  },
  {
    title: "Nile wrsETH",
    kelpKey: "kelpMilesForNileWRsEth",
    elpKey: "elPointsForNileWRsEth",
    isComingSoon: false,
    tags: ["Linea"],
    icon: "/assets/platforms/nile.jpg",
  },
  {
    title: "Blast rsETH Holdings",
    kelpKey: "kelpMilesForBlastRsEthHoldings",
    elpKey: "elPointsForBlastRsEthHoldings",
    isComingSoon: false,
    tags: ["Blast"],
    icon: "/assets/chains/blast.png",
  },
  {
    title: "Blast wrsETH Holdings",
    kelpKey: "kelpMilesForBlastWRsEthHoldings",
    elpKey: "elPointsForBlastWRsEthHoldings",
    isComingSoon: false,
    tags: ["Blast"],
    icon: "/assets/chains/blast.png",
  },
  {
    title: "Sturdy ETH",
    kelpKey: "kelpMilesForSturdyEth",
    elpKey: "elPointsForSturdyEth",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/platforms/sturdy.jpg",
  },
  {
    title: "Sturdy Mode",
    kelpKey: "kelpMilesForSturdyMode",
    elpKey: "elPointsForSturdyMode",
    isComingSoon: false,
    tags: ["Mode"],
    icon: "/assets/platforms/sturdy.jpg",
  },
  {
    title: "Bonus EL Points Campaign",
    kelpKey: "kelpMilesForBonusELpointsETH",
    elpKey: "elPointsForBonusELpointsETH",
    isComingSoon: false,
    tags: ["Ethereum", "Arbitrum"],
    icon: "/assets/kelp_icon.svg",
  },
  {
    title: "INIT Blast",
    kelpKey: "kelpMilesForInitBlast",
    elpKey: "elPointsForInitBlast",
    isComingSoon: false,
    tags: ["Blast"],
    icon: "/assets/chains/blast.png",
  },
  {
    title: "zkSync rsETH Holdings",
    kelpKey: "kelpMilesForZkSyncRsEthHoldings",
    elpKey: "elPointsForZkSyncRsEthHoldings",
    isComingSoon: false,
    tags: ["zkSync"],
    icon: "/assets/chains/zksync.svg",
  },
  {
    title: "zkSync wrsETH Holdings",
    kelpKey: "kelpMilesForZkSyncWRsEthHoldings",
    elpKey: "elPointsForZkSyncWRsEthHoldings",
    isComingSoon: false,
    tags: ["zkSync"],
    icon: "/assets/chains/zksync.svg",
  },
  {
    title: "XLayer wrsETH Holdings",
    kelpKey: "kelpMilesForXLayerWRsEthHoldings",
    elpKey: "elPointsForXLayerWRsEthHoldings",
    isComingSoon: false,
    tags: ["XLayer"],
    icon: "/assets/chains/xlayer.svg",
  },
  {
    title: "Scroll Referrer",
    kelpKey: "kelpMilesForScrollReferrer",
    elpKey: null,
    isComingSoon: false,
    tags: ["Scroll"],
    icon: "/assets/chains/scroll.svg",
  },
  {
    title: "Base Referrer",
    kelpKey: "kelpMilesForBaseReferrer",
    elpKey: null,
    isComingSoon: false,
    tags: ["Base"],
    icon: "/assets/chains/base.svg",
  },
  {
    title: "Arbitrum Referrer",
    kelpKey: "kelpMilesForArbitrumReferrer",
    elpKey: null,
    isComingSoon: false,
    tags: ["Arbitrum"],
    icon: "/assets/chains/arbitrum.svg",
  },
  {
    title: "Mode Referrer",
    kelpKey: "kelpMilesForModeReferrer",
    elpKey: null,
    isComingSoon: false,
    tags: ["Mode"],
    icon: "/assets/chains/mode.svg",
  },
  {
    title: "Blast Referrer",
    kelpKey: "kelpMilesForBlastReferrer",
    elpKey: null,
    isComingSoon: false,
    tags: ["Blast"],
    icon: "/assets/chains/blast.svg",
  },
  {
    title: "Linea Referrer",
    kelpKey: "kelpMilesForBlastReferrer",
    elpKey: null,
    isComingSoon: false,
    tags: ["Linea"],
    icon: "/assets/chains/linea.svg",
  },
  {
    title: "Optimism Referrer",
    kelpKey: "kelpMilesForOptimismReferrer",
    elpKey: null,
    isComingSoon: false,
    tags: ["Optimism"],
    icon: "/assets/chains/optimism.svg",
  },
  {
    title: "Olive",
    kelpKey: "kelpMilesForOliveMainnet",
    elpKey: "elPointsForOliveMainnet",
    isComingSoon: false,
    tags: ["Ethereum", "Arbitrum"],
    icon: "/assets/platforms/olive.svg",
  },
  {
    title: "Blackwing",
    kelpKey: "kelpMilesForBlackwingMainnet",
    elpKey: "elPointsForBlackwingMainnet",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/platforms/blackwing.jpg",
  },
  {
    title: "Layerbank wrsETH Holdings",
    kelpKey: "kelpMilesForLayerbankWRsEthHoldings",
    elpKey: "elPointsForLayerbank",
    isComingSoon: false,
    tags: ["Linea", "Scroll", "Mode"],
    icon: "/assets/platforms/layerbank.jpg",
  },
  {
    title: "Fenix Finance",
    kelpKey: "kelpMilesForFenix",
    elpKey: "elPointsForFenix",
    isComingSoon: false,
    tags: ["Blast"],
    icon: "/assets/platforms/fenix.svg",
  },
  {
    title: "Pencils",
    kelpKey: "kelpMilesForPencils",
    elpKey: "elPointsForPencils",
    isComingSoon: false,
    tags: ["Scroll"],
    icon: "/assets/platforms/pencils.jpg",
  },
  {
    title: "Ion",
    kelpKey: "kelpMilesForIon",
    elpKey: "elPointsForIon",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/platforms/ion.jpg",
  },
  {
    title: "Linea Referrer",
    kelpKey: "kelpMilesForLineaReferrer",
    elpKey: null,
    isComingSoon: false,
    tags: ["Linea"],
    icon: "/assets/chains/linea.svg",
  },
  {
    title: "ETH/rsETH Holdings",
    kelpKey: "kelpMilesForEthRsEthHoldings",
    elpKey: "elPointsForEthRsEthHoldings",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/platforms/eth_rseth.png",
  },
  {
    title: "XLayer rsETH Holdings",
    kelpKey: "kelpMilesForXLayerRsEthHoldings",
    elpKey: "elPointsForXLayerRsEthHoldings",
    isComingSoon: false,
    tags: ["XLayer"],
    icon: "/assets/chains/xlayer.svg",
  },
  {
    title: "Velodrome",
    kelpKey: "kelpMilesForVelodrome",
    elpKey: "elPointsForVelodrome",
    isComingSoon: false,
    tags: ["Optimism"],
    icon: "/assets/platforms/velorome.webp",
  },
  {
    title: "Aerodrome",
    kelpKey: "kelpMilesForAerodrome",
    elpKey: "elPointsForAerodrome",
    isComingSoon: false,
    tags: ["Base"],
    icon: "/assets/platforms/aerodrome.jpeg",
  },
  {
    title: "BladeSwap",
    kelpKey: "kelpMilesForBladeSwap",
    elpKey: "elPointsForBladeSwap",
    isComingSoon: false,
    tags: ["Blast"],
    icon: "/assets/platforms/bladeswap.png",
  },
  {
    title: "Blaster",
    kelpKey: "kelpMilesForBlaster",
    elpKey: "elPointsForBlaster",
    isComingSoon: false,
    tags: ["Blast"],
    icon: "/assets/platforms/blasterswap.webp",
  },
  {
    title: "Dolo",
    kelpKey: "kelpMilesForDolo",
    elpKey: "elPointsForDolo",
    isComingSoon: false,
    tags: ["Arbitrum"],
    icon: "/assets/platforms/dolomite.jpeg",
  },
  {
    title: "Ionic",
    kelpKey: "kelpMilesForIonic",
    elpKey: "elPointsForIonic",
    isComingSoon: false,
    tags: ["Mode"],
    icon: "/assets/platforms/ionic.png",
  },
  {
    title: "Lyra",
    kelpKey: "kelpMilesForLyra",
    elpKey: "elPointsForLyra",
    isComingSoon: false,
    tags: ["Ethereum", "Arbitrum"],
    icon: "/assets/platforms/lyra.png",
  },
  {
    title: "ZeroLend Linea",
    kelpKey: "kelpMilesForZeroLendLinea",
    elpKey: "elPointsForZeroLendLinea",
    isComingSoon: false,
    tags: ["Linea"],
    icon: "/assets/platforms/zerolend.jpg",
  },
  {
    title: "Notional",
    kelpKey: "kelpMilesForNotional",
    elpKey: "elPointsForNotional",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/platforms/notional.jpeg",
  },
  {
    title: "Pancake",
    kelpKey: "kelpMilesForPancake",
    elpKey: "elPointsForPancake",
    isComingSoon: false,
    tags: ["Arbitrum"],
    icon: "/assets/platforms/pancake.png",
  },
  {
    title: "Pendle Ethereum",
    kelpKey: "kelpMilesForPendleEthereum",
    elpKey: "elPointsForPendleEthereum",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/platforms/pendle.svg",
  },
  {
    title: "Nuri",
    kelpKey: "kelpMilesForNuri",
    elpKey: "elPointsForNuri",
    isComingSoon: false,
    tags: ["Scroll"],
    icon: "/assets/platforms/nuri.png",
  },
  {
    title: "Mendi",
    kelpKey: "kelpMilesForMendi",
    elpKey: "elPointsForMendi",
    isComingSoon: false,
    tags: ["Linea"],
    icon: "/assets/platforms/mendi.png",
  },
  {
    title: "Compound",
    kelpKey: "kelpMilesForCompound",
    elpKey: "elPointsForCompound",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/platforms/compound.png",
  },
  {
    title: "XLayer Referrer",
    kelpKey: "kelpMilesForXlayerReferrer",
    elpKey: null,
    isComingSoon: false,
    tags: ["XLayer"],
    icon: "/assets/chains/xlayer.svg",
  },
  {
    title: "zkSync Referrer",
    kelpKey: "kelpMilesForZksyncReferrer",
    elpKey: null,
    isComingSoon: false,
    tags: ["zkSync"],
    icon: "/assets/chains/zksync.svg",
  },
  {
    title: "Teahouse Linea",
    kelpKey: "kelpMilesForTeahouseLinea",
    elpKey: "elPointsForTeahouseLinea",
    isComingSoon: false,
    tags: ["Linea"],
    icon: "/assets/platforms/teahouse.png",
  },
  {
    title: "Splice",
    kelpKey: "kelpMilesForSplice",
    elpKey: "elPointsForSplice",
    isComingSoon: false,
    tags: ["Mode"],
    icon: "/assets/platforms/splice.png",
  },
  {
    title: "Astherus",
    kelpKey: "kelpMilesForAstherus",
    elpKey: "elPointsForAstherus",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/platforms/astherus.png",
  },
  {
    title: "Bracket",
    kelpKey: "kelpMilesForBracket",
    elpKey: "elPointsForBracket",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/platforms/bracket.png",
  },
  {
    title: "Harmonix",
    kelpKey: "kelpMilesForHarmonix",
    elpKey: "elPointsForHarmonix",
    isComingSoon: false,
    tags: ["Arbitrum"],
    icon: "/assets/platforms/harmonix.png",
  },
  {
    title: "Pendle Ethereum (Sept)",
    kelpKey: "kelpMilesForPendleEthereumSept",
    elpKey: "elPointsForPendleEthereumSept",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/platforms/pendle.svg",
  },
  {
    title: "Curve WeETH",
    kelpKey: "kelpMilesForCurveWeEth",
    elpKey: "elPointsForCurveWeEth",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/platforms/curve.svg",
  },
  {
    title: "Pendle Arbitrum (Sept)",
    kelpKey: "kelpMilesForPendleArbSept",
    elpKey: "elPointsForPendleArbSept",
    isComingSoon: false,
    tags: ["Arbitrum"],
    icon: "/assets/platforms/pendle.svg",
  },
  {
    title: "Zircuit (Aug)",
    kelpKey: "kelpMilesForZircuitrsETHPendleAug",
    elpKey: "elPointsForZircuitrsETHPendleAug",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/platforms/zircuit.svg",
  },
  {
    title: "Mainnet agEth Holdings",
    kelpKey: "kelpMilesForMainnetAgEthHoldings",
    elpKey: "elPointsForMainnetAgEthHoldings",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/kelp_icon.svg",
  },
  {
    title: "agEth Referrer",
    kelpKey: "kelpMilesForAgEthReferrer",
    elpKey: null,
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/kelp_icon.svg",
  },
  {
    title: "sbUSD Miles",
    kelpKey: "kelpMilesForSbusd",
    elpKey: null,
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/vaults/stableVault.svg",
  },
  {
    title: "Karak Arbitrum",
    kelpKey: "kelpMilesForKarakArb",
    elpKey: "elPointsForKarakArb",
    isComingSoon: false,
    tags: ["Arbitrum"],
    icon: "/assets/platforms/karak.svg",
  },
  {
    title: "agEth Balancer",
    kelpKey: "kelpMilesForAgEthBalancer",
    elpKey: "elPointsForAgEthBalancer",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/kelp_icon.svg",
  },
  {
    title: "agEth Pendle",
    kelpKey: "kelpMilesForAgEthPendle",
    elpKey: "elPointsForAgEthPendle",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/kelp_icon.svg",
  },
  {
    title: "Uni Optimism wstEth",
    kelpKey: "kelpMilesForUniOptWstEth",
    elpKey: "elPointsForUniOptWstEth",
    isComingSoon: false,
    tags: ["Optimism"],
    icon: "/assets/platforms/uni.svg",
  },
  {
    title: "Uni Arbitrum wstETH",
    kelpKey: "kelpMilesForUniArbWstEth",
    elpKey: "elPointsForUniArbWstEth",
    isComingSoon: false,
    tags: ["Arbitrum"],
    icon: "/assets/platforms/uni.svg",
  },
  {
    title: "Uni Arbitrum wEth",
    kelpKey: "kelpMilesForUniArbWeth",
    elpKey: "elPointsForUniArbWeth",
    isComingSoon: false,
    tags: ["Arbitrum"],
    icon: "/assets/platforms/uni.svg",
  },
  {
    title: "Balancer Optimism",
    kelpKey: "kelpMilesForBalancerOpt",
    elpKey: "elPointsForBalancerOpt",
    isComingSoon: false,
    tags: ["Optimism"],
    icon: "/assets/platforms/balancer.svg",
  },
  {
    title: "Balancer Arbitrum",
    kelpKey: "kelpMilesForBalancerArb",
    elpKey: "elPointsForBalancerArb",
    isComingSoon: false,
    tags: ["Arbitrum"],
    icon: "/assets/platforms/balancer.svg",
  },
  {
    title: "Maverick zkEra",
    kelpKey: "kelpMilesForMaverickZkEra",
    elpKey: "elPointsForMaverickZkEra",
    isComingSoon: false,
    tags: ["zkSync"],
    icon: "/assets/platforms/maverick.svg",
  },
  {
    title: "Compound Arbitrum",
    kelpKey: "kelpMilesForCompoundArb",
    elpKey: "elPointsForCompoundArb",
    isComingSoon: false,
    tags: ["Arbitrum"],
    icon: "/assets/platforms/compound.png",
  },
  {
    title: "Morpho",
    kelpKey: "kelpMilesForMorpho",
    elpKey: "elPointsForMorpho",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/services/morpho.svg",
  },
  {
    title: "Fuel Eth",
    kelpKey: "kelpMilesForFuelEth",
    elpKey: "elPointsForFuelEth",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/platforms/fuel.png",
  },
  {
    title: "Lore",
    kelpKey: "kelpMilesForLore",
    elpKey: "elPointsForLore",
    isComingSoon: false,
    tags: ["Scroll"],
    icon: "/assets/platforms/lore.png",
  },
  {
    title: "Radiant",
    kelpKey: "kelpMilesForRadiant",
    elpKey: "elPointsForRadiant",
    isComingSoon: false,
    tags: ["Arbitrum"],
    icon: "/assets/platforms/radiant.png",
  },
  {
    title: "Contango",
    kelpKey: "kelpMilesForContango",
    elpKey: "elPointsForContango",
    isComingSoon: false,
    tags: ["Ethereum", "Arbitrum"],
    icon: "/assets/platforms/contango.png",
  },

  {
    title: "Uni wstEth",
    kelpKey: "kelpMilesForUniWstEth",
    elpKey: "elPointsForUniWstEth",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/platforms/uni.svg",
  },
  {
    title: "Stella (April)",
    kelpKey: "kelpMilesForStellaArbApril",
    elpKey: "elPointsForStellaApril",
    isComingSoon: false,
    tags: ["Arbitrum"],
    icon: "/assets/platforms/stella.jpg",
  },
  {
    title: "Stella (June)",
    kelpKey: "kelpMilesForStellaArbJune",
    elpKey: "elPointsForStellaJune",
    isComingSoon: false,
    tags: ["Arbitrum"],
    icon: "/assets/platforms/stella.jpg",
  },
  {
    title: "Stella (Sept)",
    kelpKey: "kelpMilesForStellaSept",
    elpKey: "elPointsForStellaSept",
    isComingSoon: false,
    tags: ["Arbitrum"],
    icon: "/assets/platforms/stella.jpg",
  },
  {
    title: "Pendle Arb (April)",
    kelpKey: "kelpMilesForPendleArbApril",
    elpKey: "elPointsForPendleArbApril",
    isComingSoon: false,
    tags: ["Arbitrum"],
    icon: "/assets/platforms/pendle.svg",
  },

  {
    title: "KoiFin zkSync",
    kelpKey: "kelpMilesForKoiFinZksync",
    elpKey: "elPointsForKoiFinZksync",
    isComingSoon: false,
    tags: ["zksync"],
    icon: "/assets/platforms/koifin.png",
  },
  {
    title: "Silo",
    kelpKey: "kelpMilesForSilo",
    elpKey: "elPointsForSilo",
    isComingSoon: false,
    tags: ["Ethereum", "Arbitrum"],
    icon: "/assets/platforms/silo.png",
  },
  {
    title: "Asymetrix",
    kelpKey: "kelpMilesForAsymetrix",
    elpKey: "elPointsForAsymetrix",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/platforms/asymetrix.png",
  },
  {
    title: "Rho",
    kelpKey: "kelpMilesForRho",
    elpKey: "elPointsForRho",
    isComingSoon: false,
    tags: ["Scroll"],
    icon: "/assets/platforms/rho.png",
  },
  {
    title: "Ironclad",
    kelpKey: "kelpMilesForIronclad",
    elpKey: "elPointsForIronclad",
    isComingSoon: false,
    tags: ["Mode"],
    icon: "/assets/platforms/ironclad.png",
  },
  {
    title: "Syncswap",
    kelpKey: "kelpMilesForSyncswap",
    elpKey: "elPointsForSyncswap",
    isComingSoon: false,
    tags: ["zkSync"],
    icon: "/assets/platforms/syncSwap.png",
  },
  {
    title: "Factor Dao Sept",
    kelpKey: "kelpMilesForFactorDaoArbYieldSept",
    elpKey: "elPointsForFactorDaoArbYieldSept",
    isComingSoon: false,
    tags: ["Arbitrum"],
    icon: "/assets/platforms/factordao.png",
  },
  {
    title: "Spectra",
    kelpKey: "kelpMilesForSpectra",
    elpKey: "elPointsForSpectra",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/platforms/spectra.jpeg",
  },
  {
    title: "Beefy Uni wstEth",
    kelpKey: "kelpMilesForBeefyClmUniWstEth",
    elpKey: "elPointsForBeefyClmUniWstEth",
    isComingSoon: false,
    tags: ["Arbitrum"],
    icon: "/assets/platforms/beefy.png",
  },
  {
    title: "Beefy Uni wETH",
    kelpKey: "kelpMilesForBeefyClmUniWeth",
    elpKey: "elPointsForBeefyClmUniWeth",
    isComingSoon: false,
    tags: ["Arbitrum"],
    icon: "/assets/platforms/beefy.png",
  },
  {
    title: "Beefy Rms Ethx",
    kelpKey: "kelpMilesForBeefyClmRmsEthx",
    elpKey: "elPointsForBeefyClmRmsEthx",
    isComingSoon: false,
    tags: ["Arbitrum"],
    icon: "/assets/platforms/beefy.png",
  },
  {
    title: "Beefy Rms wEth",
    kelpKey: "kelpMilesForBeefyClmRmsWeth",
    elpKey: "elPointsForBeefyClmRmsWeth",
    isComingSoon: false,
    tags: ["Arbitrum"],
    icon: "/assets/platforms/beefy.png",
  },
  {
    title: "Beefy Eqb (April)",
    kelpKey: "kelpMilesForBeefyEqbApril",
    elpKey: "elPointsForBeefyEqbApril",
    isComingSoon: false,
    tags: ["Arbitrum"],
    icon: "/assets/platforms/beefy.png",
  },
  {
    title: "Beefy Eqb (June)",
    kelpKey: "kelpMilesForBeefyEqbJune",
    elpKey: "elPointsForBeefyEqbJune",
    isComingSoon: false,
    tags: ["Arbitrum"],
    icon: "/assets/platforms/beefy.png",
  },
  {
    title: "Pendle Arbitrum (Dec)",
    kelpKey: "kelpMilesForPendleArbDec",
    elpKey: "elPointsForPendleArbDec",
    isComingSoon: false,
    tags: ["Arbitrum"],
    icon: "/assets/platforms/pendle.svg",
  },
  {
    title: "Pendle Ethereum (Dec)",
    kelpKey: "kelpMilesForPendleEthDec",
    elpKey: "elPointsForPendleEthDec",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/platforms/pendle.svg",
  },
  {
    title: "Nuri agETH",
    kelpKey: "kelpMilesForNuriAgEth",
    elpKey: "elPointsForNuriAgEth",
    isComingSoon: false,
    tags: ["Scroll"],
    icon: "/assets/platforms/nuri.png",
  },
  {
    title: "Nile agETH",
    kelpKey: "kelpMilesForNileAgEth",
    elpKey: "elPointsForNileAgEth",
    isComingSoon: false,
    tags: ["Linea"],
    icon: "/assets/platforms/nile.jpg",
  },
  {
    title: "Camelot agETH",
    kelpKey: "kelpMilesForCamelotAgEth",
    elpKey: "elPointsForCamelotAgEth",
    isComingSoon: false,
    tags: ["Arbitrum"],
    icon: "/assets/platforms/camelot.png",
  },
  {
    title: "Cian Ethereum",
    kelpKey: "kelpMilesForCianEth",
    elpKey: "elPointsForCianEth",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/platforms/cian.png",
  },
  {
    title: "Cian Arbitrum",
    kelpKey: "kelpMilesForCianArb",
    elpKey: "elPointsForCianArb",
    isComingSoon: false,
    tags: ["Arbitrum"],
    icon: "/assets/platforms/cian.png",
  },
  {
    title: "Orbit",
    kelpKey: "kelpMilesForOrbit",
    elpKey: "elPointsForOrbit",
    isComingSoon: false,
    tags: ["Blast"],
    icon: "/assets/platforms/orbit.png",
  },
  {
    title: "Yay",
    kelpKey: "kelpMilesForYay",
    elpKey: "elPointsForYay",
    isComingSoon: false,
    tags: ["Scroll"],
    icon: "/assets/platforms/yay.png",
  },
  {
    title: "Moonwell",
    kelpKey: "kelpMilesForMoonwell",
    elpKey: "elPointsForMoonwell",
    isComingSoon: false,
    tags: ["Optimism", "Base"],
    icon: "/assets/platforms/moonwell.jpg",
  },
  {
    title: "hgETH",
    kelpKey: "kelpMilesForHgEth",
    elpKey: "elPointsForHgEth",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/vaults/hgeth.svg",
  },
  {
    title: "Aave Eth Reserve",
    kelpKey: "kelpMilesForAaveEthReserve",
    elpKey: "elPointsForAaveEthReserve",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/services/aave.svg",
  },
  {
    title: "Camelot wstETH",
    kelpKey: "kelpMilesForCamelotArbWstEth",
    elpKey: "elPointsForCamelotArbWstEth",
    isComingSoon: false,
    tags: ["Arbitrum"],
    icon: "/assets/platforms/camelot.png",
  },
  {
    title: "Gearbox (Eth only)",
    kelpKey: "kelpMilesForGearboxEthOnlyPool",
    elpKey: null,
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/platforms/gearbox.png",
  },
  {
    title: "Pendle Ethereum (June25)",
    kelpKey: "kelpMilesForPendleEthJune25",
    elpKey: "elPointsForPendleEthJune25",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/platforms/pendle.svg",
  },
  {
    title: "Pendle Arbitrum (June25)",
    kelpKey: "kelpMilesForPendleArbJune25",
    elpKey: "elPointsForPendleArbJune25",
    isComingSoon: false,
    tags: ["Arbitrum"],
    icon: "/assets/platforms/pendle.svg",
  },
  {
    title: "Sommelier",
    kelpKey: "kelpMilesForSommelierMainnet",
    elpKey: "elPointsForSommelierMainnet",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/platforms/sommelier.jpg",
  },
  {
    title: "Gearbox (Arbitrum)",
    kelpKey: "kelpMilesForGearboxArbitrum",
    elpKey: "elPointsForGearboxArbitrum",
    isComingSoon: false,
    tags: ["Arbitrum"],
    icon: "/assets/platforms/gearbox.png",
  },
  {
    title: "Morpho agETH",
    kelpKey: "kelpMilesForAgEthMorpho",
    elpKey: "elPointsForAgEthMorpho",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/services/morpho.svg",
  },
  {
    title: "Morpho (Base)",
    kelpKey: "kelpMilesForMorphoBase",
    elpKey: "elPointsForMorphoBase",
    isComingSoon: false,
    tags: ["Base"],
    icon: "/assets/services/morpho.svg",
  },
  {
    title: "Compound wstETH",
    kelpKey: "kelpMilesForCompoundWsteth",
    elpKey: "elPointsForCompoundWsteth",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/platforms/compound.png",
  },
  {
    title: "Compound (Base)",
    kelpKey: "kelpMilesForCompoundBase",
    elpKey: "elPointsForCompoundBase",
    isComingSoon: false,
    tags: ["Base"],
    icon: "/assets/platforms/compound.png",
  },
  {
    title: "Compound (Optimism)",
    kelpKey: "kelpMilesForCompoundOptimism",
    elpKey: "elPointsForCompoundOptimism",
    isComingSoon: false,
    tags: ["Optimism"],
    icon: "/assets/platforms/compound.png",
  },
  {
    title: "Balancer wstETH (Opt)",
    kelpKey: "kelpMilesForBalancerOptWstETH",
    elpKey: "elPointsForBalancerOptWstETH",
    isComingSoon: false,
    tags: ["Optimism"],
    icon: "/assets/platforms/balancer.svg",
  },
  {
    title: "Zircuit (wrsETH)",
    kelpKey: "kelpMilesForZircuitWRsEthHoldings",
    elpKey: "elPointsForZircuitWRsEthHoldings",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/platforms/zircuit.svg",
  },
  {
    title: "Zircuit (rsETH)",
    kelpKey: "kelpMilesForZircuitRsEthHoldings",
    elpKey: "elPointsForZircuitRsEthHoldings",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/platforms/zircuit.svg",
  },
  {
    title: "Swell (rsETH)",
    kelpKey: "kelpMilesForSwellRsEthHoldings",
    elpKey: "elPointsForSwellRsEthHoldings",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/platforms/swell.png",
  },
  {
    title: "Fluid Vaults",
    kelpKey: "kelpMilesForFluidVaults",
    elpKey: "elPointsForFluidVaults",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/platforms/fluid.png",
  },
  {
    title: "Contango Optimism",
    kelpKey: "kelpMilesForContangoOptimism",
    elpKey: "elPointsForContangoOptimism",
    isComingSoon: false,
    tags: ["Optimism"],
    icon: "/assets/platforms/contango.png",
  },
  {
    title: "Permless Depositor Rewards ",
    kelpKey: "kelpMilesForPermlessDepositerRewards",
    elpKey: "elPointsForPermlessDepositerRewards",
    isComingSoon: false,
    tags: ["Referral"],
    icon: "/assets/kelp_icon.svg",
  },
  {
    title: "Permless Referrer Rewards ",
    kelpKey: "kelpMilesForPermlessReferrerRewards",
    elpKey: "elPointsForPermlessReferrerRewards",
    isComingSoon: false,
    tags: ["Referral"],
    icon: "/assets/kelp_icon.svg",
  },
  {
    title: "Goldilocks Bera",
    kelpKey: "kelpMilesForGoldilocksBera",
    elpKey: "elPointsForGoldilocksBera",
    isComingSoon: false,
    tags: ["Bera"],
    icon: "/assets/platforms/goldilocks.png",
  },
  {
    title: "Tac cBTC",
    kelpKey: "kelpMilesForTacCBtc",
    elpKey: "elPointsForTacCBtc",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/upshift-logo.png",
  },
  {
    title: "Tac rsETH",
    kelpKey: "kelpMilesForTacRsEth",
    elpKey: "elPointsForTacRsEth",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/upshift-logo.png",
  },
  {
    title: "Aave Base Prime",
    kelpKey: "kelpMilesForAaveBasePrime",
    elpKey: "elPointsForAaveBasePrime",
    isComingSoon: false,
    tags: ["Base"],
    icon: "/assets/services/aave.svg",
  },
  {
    title: "Aave Eth Prime",
    kelpKey: "kelpMilesForAaveEthPrime",
    elpKey: "elPointsForAaveEthPrime",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/services/aave.svg",
  },
  {
    title: "Kodiak Bera ETH",
    kelpKey: "kelpMilesForKodiakBeraETH",
    elpKey: "elPointsKodiakBeraETH",
    isComingSoon: false,
    tags: ["Bera"],
    icon: "/assets/platforms/kodiak.jpg",
  },
  {
    title: "Kodiak rsETH OT",
    kelpKey: "kelpMilesForKodiakRsethOT",
    elpKey: "elPointsKodiakRsethOT",
    isComingSoon: false,
    tags: ["Bera"],
    icon: "/assets/platforms/kodiak.jpg",
  },
  {
    title: "hgETH Balancer",
    kelpKey: "kelpMilesForHgEthBalancer",
    elpKey: "elPointsForHgEthBalancer",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/platforms/balancer.svg",
  },
  {
    title: "hgETH RsEth Balancer",
    kelpKey: "kelpMilesForHgEthRsEthBalancer",
    elpKey: "elPointsForHgEthRsEthBalancer",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/platforms/balancer.svg",
  },
  {
    title: "Pendle hgETH June 25",
    kelpKey: "kelpMilesForPendleHgethJune25",
    elpKey: "elPointsForPendleHgethJune25",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/platforms/pendle.svg",
  },
  {
    title: "Dolomite",
    kelpKey: "kelpMilesForDolomite",
    elpKey: "elPointsForDolomite",
    isComingSoon: false,
    tags: ["Arbitrum"],
    icon: "/assets/platforms/dolomite.jpeg",
  },
  {
    title: "Bera rsEth Holdings",
    kelpKey: "kelpMilesForBeraRsEthHoldings",
    elpKey: "elPointsForBeraRsEthHoldings",
    isComingSoon: false,
    tags: ["Bera"],
    icon: "/assets/platforms/bera.jpeg",
  },
  {
    title: "Uni rsEth Holdings",
    kelpKey: "kelpMilesForUniRsEthHoldings",
    elpKey: "elPointsForUniRsEthHoldings",
    isComingSoon: false,
    tags: ["Ethereum", "Arbitrum"],
    icon: "/assets/platforms/uni.svg",
  },
  {
    title: "Hemi rsEth Holdings",
    kelpKey: "kelpMilesForHemiRsEthHoldings",
    elpKey: "elPointsForHemiRsEthHoldings",
    isComingSoon: false,
    tags: ["Hemi Mainnet"],
    icon: "/assets/platforms/hemi.png",
  },
  {
    title: "UniChain UniswapV4",
    kelpKey: "kelpMilesForUniChainUniswapV4",
    elpKey: "elPointsForUniChainUniswapV4",
    isComingSoon: false,
    tags: ["Unichain"],
    icon: "/assets/platforms/unichain.png",
  },
  {
    title: "Pendle Eth June 26",
    kelpKey: "kelpMilesForPendleEthJune26",
    elpKey: "elPointsforPendleEthJune26",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/platforms/pendle.svg",
  },
  {
    title: "Pendle Arb June 26",
    kelpKey: "kelpMilesForPendleArbJune26",
    elpKey: "elPointsforPendleArbJune26",
    isComingSoon: false,
    tags: ["Arbitrum"],
    icon: "/assets/platforms/pendle.svg",
  },
  {
    title: "Spark",
    kelpKey: "kelpMilesForSpark",
    elpKey: "elPointsForSpark",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/platforms/spark.png",
  },
  {
    title: "Euler",
    kelpKey: "kelpMilesForEuler",
    elpKey: "elPointsForEuler",
    isComingSoon: false,
    tags: ["Unichain", "Ethereum"],
    icon: "/assets/platforms/euler.png",
  },
  {
    title: "Kodiak Bera ETH",
    kelpKey: "kelpMilesForKodiakBeraETH",
    elpKey: "elPointsKodiakBeraETH",
    isComingSoon: false,
    tags: ["Bera"],
    icon: "/assets/platforms/kodiak.jpg",
  },
  {
    title: "Kodiak rsETH OT",
    kelpKey: "kelpMilesForKodiakRsethOT",
    elpKey: "elPointsKodiakRsethOT",
    isComingSoon: false,
    tags: ["Bera"],
    icon: "/assets/platforms/kodiak.jpg",
  },
  {
    title: "Aave Arb",
    kelpKey: "kelpMilesForAaveArb",
    elpKey: "elPointsForAaveArb",
    isComingSoon: false,
    tags: ["Arbitrum"],
    icon: "/assets/services/aave.svg",
  },
  {
    title: "hgEth Euler",
    kelpKey: "kelpMilesForHgEthEuler",
    elpKey: "elPointsForHgEthEuler",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/vaults/hgeth.svg",
  },
  {
    title: "Beets Balancer",
    kelpKey: "kelpMilesForBeetsBalancer",
    elpKey: "elPointsForBeetsBalancer",
    isComingSoon: false,
    tags: ["Optimism"],
    icon: "/assets/platforms/balancer.svg",
  },
  {
    title: "Unichain Morpho",
    kelpKey: "kelpMilesForUnichainMorpho",
    elpKey: "elPointsForUnichainMorpho",
    isComingSoon: false,
    tags: ["Unichain"],
    icon: "/assets/services/morpho.svg",
  },
  {
    title: "Tac rsETH Holdings",
    kelpKey: "kelpMilesForTacRsEthHoldings",
    elpKey: "elPointsForTacRsEthHoldings",
    isComingSoon: false,
    tags: ["Tac"],
    icon: "/assets/vaults/upshift-logo.png",
  },
  {
    title: "Contango agETH",
    kelpKey: "kelpMilesForContangoAgEth",
    elpKey: null,
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/platforms/contango.png",
  },
  {
    title: "Gain",
    kelpKey: "kelpMilesForGain",
    elpKey: "elPointsForGain",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/favicons/Gain.png",
  },
  {
    title: "sbUSD",
    kelpKey: "kelpMilesForSbusd",
    elpKey: null,
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/vaults/stableVault.svg",
  },
  {
    title: "Beets Balancer",
    kelpKey: "kelpMilesForBeetsBalancer",
    elpKey: "elPointsForBeetsBalancer",
    isComingSoon: false,
    tags: ["Optimism"],
    icon: "/assets/platforms/balancer.svg",
  },
  {
    title: "Fluid Vaults Base",
    kelpKey: "kelpMilesForFluidVaultsBase",
    elpKey: "elPointsForFluidVaultsBase",
    isComingSoon: false,
    tags: ["Base"],
    icon: "/assets/platforms/fluid.png",
  },
  {
    title: "Fluid Vaults Arbitrum",
    kelpKey: "kelpMilesForFluidVaultsArb",
    elpKey: "elPointsForFluidVaultsArb",
    isComingSoon: false,
    tags: ["Arbitrum"],
    icon: "/assets/platforms/fluid.png",
  },
  {
    title: "Fluid Vaults Plasma",
    kelpKey: "kelpMilesForFluidVaultsPlasma",
    elpKey: "elPointsForFluidVaultsPlasma",
    isComingSoon: false,
    tags: ["Plasma"],
    icon: "/assets/platforms/fluid.png",
  },
  {
    title: "hgETH Euler",
    kelpKey: "kelpMilesForHgEthEuler",
    elpKey: "elPointsForHgEthEuler",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/platforms/euler.png",
  },
  {
    title: "Plasma wrsETH Holdings",
    kelpKey: "kelpMilesForPlasmaWRsEthHoldings",
    elpKey: "elPointsForPlasmaWRsEthHoldings",
    isComingSoon: false,
    tags: ["Plasma"],
    icon: "/assets/chains/plasma.png",
  },
  {
    title: "Plasma rsETH Holdings",
    kelpKey: "kelpMilesForPlasmaRsEthHoldings",
    elpKey: "elPointsForPlasmaRsEthHoldings",
    isComingSoon: false,
    tags: ["Plasma"],
    icon: "/assets/chains/plasma.png",
  },
  {
    title: "Aave Linea",
    kelpKey: "kelpMilesForAaveLinea",
    elpKey: "elPointsForAaveLinea",
    isComingSoon: false,
    tags: ["Linea"],
    icon: "/assets/services/aave.svg",
  },
  {
    title: "Avalanche rsETH Holdings",
    kelpKey: "kelpMilesForAvalanceRsEthHoldings",
    elpKey: "elPointsForAvalanceRsEthHoldings",
    isComingSoon: false,
    tags: ["Avalanche"],
    icon: "/assets/chains/avalanche.png",
  },
  {
    title: "Avalanche wrsETH Holdings",
    kelpKey: "kelpMilesForAvalanceWRsEthHoldings",
    elpKey: "elPointsForAvalanceWRsEthHoldings",
    isComingSoon: false,
    tags: ["Avalanche"],
    icon: "/assets/chains/avalanche.png",
  },
  {
    title: "Sonic rsETH Holdings",
    kelpKey: "kelpMilesForSonicRsEthHoldings",
    elpKey: "elPointsForSonicRsEthHoldings",
    isComingSoon: false,
    tags: ["Sonic"],
    icon: "/assets/chains/sonic.png",
  },
  {
    title: "Sonic wrsETH Holdings",
    kelpKey: "kelpMilesForSonicWRsEthHoldings",
    elpKey: "elPointsForSonicWRsEthHoldings",
    isComingSoon: false,
    tags: ["Sonic"],
    icon: "/assets/chains/sonic.png",
  },
  {
    title: "Aave Plasma",
    kelpKey: "kelpMilesForAavePlasma",
    elpKey: "elPointsForAavePlasma",
    isComingSoon: false,
    tags: ["Plasma"],
    icon: "/assets/services/aave.svg",
  },
  {
    title: "Blackhole",
    kelpKey: "kelpMilesForBlackhole",
    elpKey: "elPointsForBlackhole",
    isComingSoon: false,
    tags: ["Ethereum"],
    icon: "/assets/platforms/blackhole.png",
  },
  {
    title: "Aave Arbitrum",
    kelpKey: "kelpMilesForAaveArb",
    elpKey: "elPointsForAaveArb",
    isComingSoon: false,
    tags: ["Arbitrum"],
    icon: "/assets/services/aave.svg",
  },
  {
    title: "Aave Avalanche",
    kelpKey: "kelpMilesForAaveAvax",
    elpKey: "elPointsForAaveAvax",
    isComingSoon: false,
    tags: ["Avalanche"],
    icon: "/assets/services/aave.svg",
  },
  {
    title: "Unichain Morpho",
    kelpKey: "kelpMilesForUnichainMorpho",
    elpKey: "elPointsForUnichainMorpho",
    isComingSoon: false,
    tags: ["Unichain"],
    icon: "/assets/services/morpho.svg",
  },
  {
    title: "Aave Arbitrum",
    kelpKey: "kelpMilesForAaveArb",
    elpKey: "elPointsForAaveArb",
    isComingSoon: false,
    tags: ["Arbitrum"],
    icon: "/assets/services/aave.svg",
  },
];

export const INITIAL_REWARDS_DATA = {
  kelpMilesForRsEthHoldings: "",
  bonus100kKelpMiles: "",
  kelpMiles: "", // TOTAL
  elPoints: "", // TOTAL
  kmForLastDay: "",
  elPointsForLastDay: "",
  elPointsForRsEthHoldings: "",
  elPointsForPendle: "",
  eqbVoters: "",
  penpieVoters: "",
  kelpMilesForReferrer: "",
  elPointsMissing: "",
  kelpMilesForBonusELpointsETH: "",
  elPointsForBonusELpointsETH: "",
  kelpMilesForPendleEthereumSept: "",
  kelpMilesForZircuitrsETHPendle: "",
};

export const BRIDGE_AGETH_OPTIONS = [
  {
    id: 42161,
    to: "Arbitrum",
    icon: "/assets/bridge/arbitrum.svg",
    link: "https://app.squidrouter.com/?chains=1%2C42161&tokens=0xe1b4d34e8754600962cd944b535180bd758e6c2e%2C0x1bd0fe8e92a157d3ef66c9fb9e38621252b407c2",
    isExternal: true,
  },
  {
    id: 534352,
    to: "Scroll",
    icon: "/assets/chains/scroll.svg",
    link: "https://app.squidrouter.com/?chains=1%2C534352&tokens=0xe1b4d34e8754600962cd944b535180bd758e6c2e%2C0x1bd0fe8e92a157d3ef66c9fb9e38621252b407c2",
    isExternal: true,
  },
  {
    id: 59144,
    to: "Linea",
    icon: "/assets/chains/linea.svg",
    link: "https://app.squidrouter.com/?chains=1%2C59144&tokens=0xe1b4d34e8754600962cd944b535180bd758e6c2e%2C0x1bd0fe8e92a157d3ef66c9fb9e38621252b407c2",
    isExternal: true,
  },
  {
    id: 1,
    to: "Ethereum",
    icon: "/assets/chains/ethereum.svg",
    link: "https://app.squidrouter.com/?chains=1%2C59144&tokens=0xe1b4d34e8754600962cd944b535180bd758e6c2e%2C0x1bd0fe8e92a157d3ef66c9fb9e38621252b407c2",
    isExternal: true,
  },
];

export const BRIDGE_OPTIONS = [
  {
    id: 42161,
    to: "Arbitrum",
    icon: "/assets/bridge/arbitrum.svg",
    link: "https://stargate.finance/transfer",
    isExternal: true,
  },
  {
    id: 10,
    to: "Optimism",
    icon: "/assets/chains/optimism.svg",
    link: "https://app.transporter.io/?tab=token&token=rsETH",
    isExternal: true,
  },
  {
    id: 169,
    to: "Manta Pacific",
    icon: "/assets/bridge/manta-pacific.svg",
    link: "https://stargate.finance/transfer",
    isExternal: true,
  },
  {
    id: 81457,
    to: "Blast",
    icon: "/assets/bridge/blast.svg",
    link: "https://stargate.finance/transfer",
    isExternal: true,
  },
  {
    id: 34443,
    to: "Mode",
    icon: "/assets/bridge/mode.svg",
    link: "https://stargate.finance/transfer",
    isExternal: true,
  },
  {
    id: 534352,
    to: "Scroll",
    icon: "/assets/chains/scroll.svg",
    link: "https://stargate.finance/transfer",
    isExternal: true,
  },
  {
    id: 59144,
    to: "Linea",
    icon: "/assets/chains/linea.svg",
    link: "https://app.transporter.io/?tab=token&token=rsETH",
    isExternal: true,
  },
  {
    id: 8453,
    to: "Base",
    icon: "/assets/chains/base.svg",
    link: "https://stargate.finance/transfer",
    isExternal: true,
  },
  {
    id: 324,
    to: "zkSync Era",
    icon: "/assets/chains/zksync.svg",
    link: "https://stargate.finance/transfer",
    isExternal: true,
  },
  {
    id: 48900,
    to: "Zircuit",
    icon: "/assets/chains/zircuit.svg",
    link: "https://app.transporter.io/?tab=token&token=rsETH",
    isExternal: true,
  },
  {
    id: 1,
    to: "Ethereum",
    icon: "/assets/chains/ethereum.svg",
    link: "https://stargate.finance/transfer",
    isExternal: true,
  },
  {
    id: 196,
    to: "X Layer",
    icon: "/assets/chains/xlayer.svg",
    link: "https://stargate.finance/transfer",
    isExternal: true,
  },
  {
    id: 130,
    to: "Unichain",
    icon: "/assets/chains/unichain.svg",
    link: "https://stargate.finance/bridge?srcChain=ethereum&srcToken=0xA1290d69c65A6Fe4DF752f95823fae25cB99e5A7&dstChain=unichain&dstToken=0xc3eACf0612346366Db554C991D7858716db09f58",
    isExternal: true,
  },
  {
    id: 239,
    to: "Tac",
    icon: "/assets/chains/tac.svg",
    link: "https://stargate.finance/transfer",
    isExternal: true,
  },
  {
    id: 43114,
    to: "Avalanche",
    icon: "/assets/chains/avalanche.png",
    link: "https://stargate.finance/transfer",
    isExternal: true,
  },
  {
    id: 146,
    to: "Sonic",
    icon: "/assets/chains/sonic.png",
    link: "https://stargate.finance/transfer",
    isExternal: true,
  },
  {
    id: 57073,
    to: "Ink",
    icon: "/assets/chains/ink.svg",
    link: "https://stargate.finance/transfer",
    isExternal: true,
  },
  {
    id: 9745,
    to: "Plasma",
    icon: "/assets/chains/plasma.png",
    link: "https://stargate.finance/transfer",
    isExternal: true,
  },
  {
    id: 988,
    to: "Stable",
    icon: "/assets/chains/stable.webp",
    link: "https://stargate.finance/transfer",
    isExternal: true,
  },
  {
    id: 5000,
    to: "Mantle",
    icon: "/assets/chains/mantle.svg",
    link: "https://stargate.finance/transfer",
    isExternal: true,
  },
  {
    id: 143,
    to: "Monad",
    icon: "/assets/chains/monad.png",
    link: "https://stargate.finance/transfer",
    isExternal: true,
  },
  {
    id: 4326,
    to: "MegaETH",
    icon: "/assets/chains/megaeth.webp",
    link: "https://stargate.finance/transfer",
    isExternal: true,
  },
];

export const STADER_CLIENT_ID = "8e156db4-59ab-46e8-9ef4-17aac2d9fb96";

export type STEPS_TYPE = {
  text: string;
  pair_from?: string;
  pair_to?: string;
};

export const STEPS_STADER_LEDGER_TXS: Array<STEPS_TYPE> = [
  { text: "Initiate stake", pair_from: "ETH", pair_to: "ETHx" },
  { text: "Approve restake" },
  { text: "Initiate restake", pair_from: "ETHx", pair_to: "rsETH" },
];

export const chainDefiSectionMapping: IIndexableNumber = {
  1: "Ethereum",
  10: "Optimism",
  11155111: "Ethereum", // Sepolia testnet - uses Ethereum section data
  196: "Ethereum",
  324: "zkSync",
  8453: "Base",
  42161: "Arbitrum",
  34443: "Mode",
  59144: "Linea",
  81457: "Blast",
  534352: "Scroll",
  43114: "Avalanche",
  146: "Sonic",
  5000: "Mantle",
  143: "Monad",
  4326: "MegaETH",
};

export const IS_STAKING_PAUSED = false; // change to true when staking deposit/withdrawal is paused
